import * as React from 'react'

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import { createPortal } from 'react-dom'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ComposerFooter from './ComposerFooter'
import ComposerSatellites from './ComposerSatellites'
import { useComposerSheetFrame } from './useComposerSheetFrame'
import WorkflowComposerStore, {
  type ComposerSheetSnap,
} from './WorkflowComposer.store'
import WorkflowComposerCard from './WorkflowComposerCard'

interface IProps {
  store: WorkflowComposerStore
}

// Drive heights via maxHeight, not vaul snap points: vaul's snap transform translates
// this short content-sized sheet fully off-screen.
const COLLAPSED_MAX_H = 56
// Intermediate "peek" on markup-tool engage: clears the map for drawing while keeping
// the creation context visible.
const PEEK_MAX_H = 232
// Map/pin strip kept visible above the sheet while the keyboard is open.
const KEYBOARD_MAP_PEEK_PX = 160

// Keyboard overlap (px): iOS doesn't shrink the layout viewport, so a fixed
// bottom:0 bar hides behind the keyboard; the action bar lifts by this.
function useKeyboardInset(): number {
  const [inset, setInset] = React.useState(0)
  React.useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = (): void =>
      // Don't subtract offsetTop: a standalone PWA scrolls the document open and
      // subtracting that zeroed the lift; Safari's offsetTop is 0, so this fits both.
      setInset(Math.max(0, window.innerHeight - vv.height))
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])
  return inset
}

// A tap outside doesn't blur the Title, so its keyboard sticks. Deferred so a tap that
// focuses another input wins; otherwise focus moves to the sheet container — dismissing
// from inside the composer, so vaul can't refocus the Title.
function useDismissComposerKeyboardOnOutsideTap(
  sheetRef: React.RefObject<HTMLElement>,
  barRef: React.RefObject<HTMLElement>,
): void {
  React.useEffect(() => {
    let timer = 0
    const onPointerDown = (e: PointerEvent): void => {
      const sheet = sheetRef.current
      const active = document.activeElement
      if (!sheet || !active || active === sheet || !sheet.contains(active)) {
        return
      }
      const target = e.target as Node | null
      if (sheet.contains(target) || barRef.current?.contains(target)) return
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const s = sheetRef.current
        const current = document.activeElement
        if (s && current && current !== s && s.contains(current)) s.focus()
      }, 0)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [sheetRef, barRef])
}

// vaul's Radix focus-scope snaps focus back to the Title; a capture listener hides map
// focus moves from it so the keyboard doesn't bounce back.
function useReleaseComposerFocusOverMap(): void {
  React.useEffect(() => {
    const inMap = (n: EventTarget | null): boolean =>
      n instanceof Element && !!n.closest('#map, [data-markup-text-editable]')
    const onFocusIn = (e: FocusEvent): void => {
      if (inMap(e.target)) e.stopPropagation()
    }
    const onFocusOut = (e: FocusEvent): void => {
      if (inMap(e.relatedTarget)) e.stopPropagation()
    }
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('focusout', onFocusOut, true)
    return () => {
      document.removeEventListener('focusin', onFocusIn, true)
      document.removeEventListener('focusout', onFocusOut, true)
    }
  }, [])
}

// Map overlays don't consume touch and the body isn't position-locked (noBodyStyles),
// so a drag on them rubber-bands the document; kill the bounce while the composer is up.
function useLockDocumentOverscroll(): void {
  React.useEffect(() => {
    const { style } = document.documentElement
    const prev = style.overscrollBehavior
    style.overscrollBehavior = 'none'
    return () => {
      style.overscrollBehavior = prev
    }
  }, [])
}

// Peek on markup-tool engage to clear the map — only from 'expanded', so a manual
// collapse (the user chose max map) or an already-peeked sheet is left alone.
function usePeekOnMarkupEngage(store: WorkflowComposerStore): void {
  React.useEffect(
    () =>
      reaction(
        () => store.isMarkupEngaged,
        engaged => {
          if (engaged && store.snap === 'expanded') store.peekSheet()
        },
      ),
    [store],
  )
}

// The grab handle drives the height directly: vaul's own snap drag needs a viewport-tall
// drawer, which breaks the keyboard layout. Peek is programmatic only, never a drag target.
function useSheetDrag(
  store: WorkflowComposerStore,
  sheetRef: React.RefObject<HTMLDivElement>,
  contentRef: React.RefObject<HTMLDivElement>,
): {
  dragHeight: number | null
  isDragging: boolean
  dragHandlers: {
    onPointerDown: (e: React.PointerEvent) => void
    onPointerMove: (e: React.PointerEvent) => void
    onPointerUp: (e: React.PointerEvent) => void
  }
} {
  const [dragHeight, setDragHeight] = React.useState<number | null>(null)
  const start = React.useRef<{ y: number; h: number } | null>(null)
  const ceiling = React.useRef(0)
  const latest = React.useRef(0)
  const dragHandlers = {
    onPointerDown: (e: React.PointerEvent): void => {
      // Keep the press off vaul's own drag handler; start from the sheet's ACTUAL
      // height so switching to an explicit height doesn't jump.
      e.stopPropagation()
      const sheetH =
        sheetRef.current?.getBoundingClientRect().height ?? PEEK_MAX_H
      const contentH = contentRef.current?.scrollHeight ?? sheetH
      ceiling.current = Math.min(contentH, Math.round(window.innerHeight * 0.8))
      start.current = { y: e.clientY, h: sheetH }
      latest.current = sheetH
      setDragHeight(sheetH)
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    onPointerMove: (e: React.PointerEvent): void => {
      if (!start.current) return
      e.stopPropagation()
      const next = Math.max(
        COLLAPSED_MAX_H,
        Math.min(
          ceiling.current,
          start.current.h - (e.clientY - start.current.y),
        ),
      )
      latest.current = next
      setDragHeight(next)
    },
    onPointerUp: (e: React.PointerEvent): void => {
      if (!start.current) return
      e.stopPropagation()
      const settled = latest.current
      start.current = null
      setDragHeight(null)
      if (settled > (COLLAPSED_MAX_H + ceiling.current) / 2) store.maximize()
      else store.minimize()
    },
  }
  return { dragHeight, isDragging: dragHeight !== null, dragHandlers }
}

// vaul bottom-sheet, three store-driven heights; non-modal click-through keeps the map
// live, noBodyStyles avoids collapsing AutoSizer (STX1-443).
const MobileWorkflowComposer: React.FC<IProps> = observer(({ store }) => {
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const actionBarRef = React.useRef<HTMLDivElement>(null)
  const [sheetContainer, setSheetContainer] =
    React.useState<HTMLDivElement | null>(null)
  const keyboardInset = useKeyboardInset()
  useDismissComposerKeyboardOnOutsideTap(sheetRef, actionBarRef)
  useReleaseComposerFocusOverMap()
  useLockDocumentOverscroll()
  usePeekOnMarkupEngage(store)

  // Content-sized wrapper: its scrollHeight is the drag's pull ceiling, measured at
  // drag start (expanded height is CSS-auto, so no continuous measurement).
  const contentRef = React.useRef<HTMLDivElement>(null)
  const { dragHeight, isDragging, dragHandlers } = useSheetDrag(
    store,
    sheetRef,
    contentRef,
  )
  useComposerSheetFrame(store, sheetRef, actionBarRef, isDragging)

  // Focusing the sheet container dismisses the Title's keyboard while staying inside
  // the focus trap, so it isn't snapped back.
  const dismissKeyboard = React.useCallback((): void => {
    sheetRef.current?.focus()
  }, [])

  // Keyed on `snap`, NOT isMinimized: peek→collapsed never flips the boolean, so the
  // keyboard stayed up. Pin re-framing instead follows the sheet's settled top
  // (useComposerSheetFrame), which sees every snap move.
  const prevSnapRef = React.useRef<ComposerSheetSnap>('collapsed')
  React.useEffect(() => {
    const prev = prevSnapRef.current
    prevSnapRef.current = store.snap
    if (prev === store.snap) return
    if (store.snap === 'expanded') store.exitDrawing()
    else dismissKeyboard()
  }, [store, store.snap, dismissKeyboard])

  // Resize/lift for the keyboard ONLY when the sheet's own field (the Title) is
  // focused — a text-markup keyboard must leave the sheet at its snap.
  const [sheetInputFocused, setSheetInputFocused] = React.useState(false)
  React.useEffect(() => {
    const sync = (): void => {
      const el = sheetRef.current
      const active = document.activeElement
      setSheetInputFocused(
        !!el && !!active && el.contains(active) && active.tagName === 'INPUT',
      )
    }
    document.addEventListener('focusin', sync)
    document.addEventListener('focusout', sync)
    return () => {
      document.removeEventListener('focusin', sync)
      document.removeEventListener('focusout', sync)
    }
  }, [])
  // Requires a measured keyboard too: Safari only reports the height when the Title
  // is focused from a settled layout.
  const sheetKeyboardOpen = sheetInputFocused && keyboardInset > 0

  // Fixed to the viewport so Cancel/Submit stay reachable at any snap; it escapes
  // the sheet's transform by living beside it, not inside it.
  const actionBar = (
    <div
      ref={actionBarRef}
      // Portaled outside the drawer's focus scope, so blurring the Title here
      // makes the scope refocus it and select-all. Keep focus put; click still fires.
      onMouseDown={e => e.preventDefault()}
      className="tw:pointer-events-auto tw:z-50 tw:flex tw:flex-col tw:gap-1 tw:border-t tw:border-border tw:bg-popover tw:px-4 tw:pt-2 tw:pb-safe-3"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // Lift via padding-bottom (not `bottom`) so the opaque bar fills to the
        // viewport edge and masks the rows Safari leaves showing beneath a floating bar.
        paddingBottom: keyboardInset > 0 ? keyboardInset : undefined,
      }}
    >
      <div className="tw:flex tw:items-center tw:gap-2 tw:pb-2">
        <Button
          variant="outline"
          size="icon"
          onClick={store.snap === 'collapsed' ? store.maximize : store.minimize}
          aria-label={
            store.snap === 'collapsed'
              ? Localization.translator.open
              : Localization.translator.minimize
          }
        >
          {store.snap === 'collapsed' ? (
            <ChevronUpIcon className="tw:size-4" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="tw:size-4" aria-hidden="true" />
          )}
        </Button>
        <ComposerFooter
          isMobileEnv
          canSubmit={store.canSubmit}
          isSubmitting={store.isSubmitting}
          onCancel={() => {
            dismissKeyboard()
            store.close()
          }}
          onSubmit={() => {
            dismissKeyboard()
            void store.submit()
          }}
        />
      </div>
    </div>
  )

  return (
    <>
      {/* The sheet and its bar share one portal: the bar follows the sheet's slot, so
          at the shared tier it paints above it, and overlays opened later (pickers,
          markup sheets) append after both. */}
      {createPortal(
        <div>
          <div ref={setSheetContainer} />
          {actionBar}
        </div>,
        document.body,
      )}
      {sheetContainer && (
        <Drawer
          open
          container={sheetContainer}
          modal={false}
          dismissible={false}
          noBodyStyles
          // The title sits above the keyboard in the sheet; skip vaul's scroll-into-view jump.
          repositionInputs={false}
        >
          <DrawerContent
            ref={sheetRef}
            // Focus target for dismissing the Title's keyboard on an outside tap.
            tabIndex={-1}
            // Disable vaul's own drag sheet-wide — the grab strip drives it; otherwise
            // vaul rubber-bands the sheet on touch.
            data-vaul-no-drag=""
            className="tw:pointer-events-auto"
            // Expanded stays CSS-auto (maxHeight) so the sheet hugs the form with no
            // measurement; the sheet's own keyboard shrinks it above the inset and lifts it.
            style={{
              ...(dragHeight != null
                ? { height: dragHeight }
                : sheetKeyboardOpen
                  ? {
                      maxHeight:
                        window.innerHeight -
                        keyboardInset -
                        KEYBOARD_MAP_PEEK_PX,
                    }
                  : store.snap === 'collapsed'
                    ? { height: COLLAPSED_MAX_H }
                    : store.snap === 'peek'
                      ? { height: PEEK_MAX_H }
                      : { maxHeight: '80vh' }),
              transition: isDragging
                ? 'none'
                : 'height 0.3s cubic-bezier(0.32, 0.72, 0, 1), max-height 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              ...(sheetKeyboardOpen && { bottom: keyboardInset }),
            }}
            // Auto-grabbing the Title pops the iOS keyboard over the collapsed sheet.
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            {/* Grab strip: free-drags the sheet height — touch-none so it doesn't scroll,
              data-vaul-no-drag so vaul doesn't also drag from here. */}
            <div
              {...dragHandlers}
              data-vaul-no-drag=""
              className="tw:absolute tw:inset-x-0 tw:top-0 tw:z-10 tw:h-7 tw:cursor-grab tw:touch-none tw:active:cursor-grabbing"
              aria-hidden="true"
            />
            <DrawerTitle className="tw:sr-only">
              {Localization.translator.createWorkflow}
            </DrawerTitle>
            <DrawerDescription className="tw:sr-only">
              {Localization.translator.createWorkflow}
            </DrawerDescription>
            {/* In peek, touching the form exits drawing; a keyboard input (the Title) must
              edit IN PLACE — maximizing while its keyboard opens makes iOS drop the
              keyboard height — so it exits without changing the snap. */}
            <div
              className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-y-auto tw:overscroll-contain"
              onClick={
                store.snap === 'peek'
                  ? e => {
                      if ((e.target as Element).closest('input, textarea')) {
                        store.exitDrawing()
                      } else {
                        store.maximize()
                      }
                    }
                  : undefined
              }
            >
              <div ref={contentRef} className="tw:shrink-0">
                <WorkflowComposerCard store={store} isMobileEnv />
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}
      <ComposerSatellites
        store={store.formStore}
        isMobileEnv
        allowedTypeIds={store.offerableWorkflowTypeIds}
      />
    </>
  )
})

export default MobileWorkflowComposer
